import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, ShieldQuestion } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchMyCertification } from '@/data/advisor-certification';

export default function CertificationCard() {
  const state = useQuery({
    queryKey: ['advisor-certification'],
    queryFn: fetchMyCertification,
    retry: false,
  });

  const data = state.data;
  if (state.isLoading || !data?.has_profile) return null;

  if (data.is_certified) {
    return (
      <Card className="border-emerald-200" data-testid="certification-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BadgeCheck className="h-5 w-5 text-emerald-600" />
            Certified advisor
            <Badge variant="secondary" data-testid="certification-level">
              {data.certification_display}
            </Badge>
          </CardTitle>
          <CardDescription>
            {data.is_listed
              ? 'You are in the public directory, and customers signing up can choose you.'
              : 'Publish your profile and you will appear in the public directory.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const total = data.required_total || 0;
  const passed = data.required_passed || 0;

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="certification-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldQuestion className="h-5 w-5 text-amber-600" />
          Your profile is not in the directory yet
        </CardTitle>
        <CardDescription>
          You can use everything here: add clients, map how they work, write specifications, publish your profile and share its link. Certification lists you in the public directory, where people who do not know you yet can find you.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-amber-500 transition-all"
              style={{ width: `${total ? (passed / total) * 100 : 0}%` }}
            />
          </div>
          <span className="text-sm text-muted-foreground" data-testid="training-progress">
            {passed} of {total} modules passed
          </span>
        </div>

        <Button asChild size="sm" data-testid="certification-open">
          <Link to="/advisor/certification">
            {data.call
              ? 'See your certification call'
              : data.training_complete
                ? 'Book your certification call'
                : passed > 0
                  ? 'Carry on with the training'
                  : 'Start the training'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
